import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import { getRoundName } from '../../../utils/courtSchedular';
import { Select } from '../../../components/Form';
import EventDeskPostScore from '../EventDeskPostScore';
import NextAvailableSidePanel from './NextAvailableSidePanel';
import { NA_POST_SCORE_FAILURE } from '../../../actions/actionTypes/next-available-types';

import styles from './nextAvailable.scss';
import { utrTypeIds } from '../../../utils/eventConstants';

const MatchPostScoreSidePanel = (props) => {
    const {
        className,
        eventId,
        closeSideBar,
        match,
        officials,
        sportTypeId,
        utrTypeId,
        clubTierType,
        onPostSubmit,
        isSubmitting,
        savingError,
        playersDictByMemberId,
        teamsDict,
    } = props;

    const dispatch = useDispatch();
    const postScoreRef = useRef(null);
    const [selectedOfficial, setSelectedOfficial] = useState(null);
    const [scoreChanged, setScoreChanged] = useState(false);
    const [scoresAreValid, setScoresAreValid] = useState(false);

    const handleRefereeChange = (value) => {
        setSelectedOfficial(value);
    };

    const onSubmitClick = async () => {
        const hasFailedValidation = postScoreRef.current.validatePostScore();
        if (hasFailedValidation?.error) {
            dispatch({
                type: NA_POST_SCORE_FAILURE,
                payload: hasFailedValidation?.error,
            });
            return;
        }

        const canSubmitPostScoreResult = postScoreRef.current?.canSubmitPostScoreResult();
        const postScoreResult = postScoreRef.current?.getPostScoreResult();
        const requestBody = {
            postScoreResult: canSubmitPostScoreResult ? postScoreResult : null,
            officialId: selectedOfficial,
        };

        onPostSubmit(eventId, match.id, requestBody);
    };

    const onScoreChanged = () => {
        setScoreChanged(true);
    };

    const isDisabled = isSubmitting || !scoreChanged || !scoresAreValid;

    return (
        <NextAvailableSidePanel
            className={className}
            closeSideBar={closeSideBar}
        >
            <div className={styles.header} data-testid="next-available.match-post-score.title">
                {match.parentDrawName}
            </div>
            <div className={styles.content}>
                <div className={styles.section}>
                    <h6 className={styles.sectionTitle} data-testid="next-available.match-post-score.draw-round">
                        {`${match.drawName}, ${getRoundName(match)}`}
                    </h6>
                    {savingError && (
                        <div>
                            <p className={styles.scoreErrorText} data-testid="next-available.match-post-score.error-title">Error: Invalid score</p>
                            <p className={styles.scoreErrorTextAction} data-testid="next-available.match-post-score.error-detail">{savingError}</p>
                        </div>
                    )}
                </div>
                <div className={styles.section}>
                    <h6 className={styles.sectionTitle} data-testid="next-available.match-post-score.outcome-title">OUTCOME</h6>
                    <Select
                        id="matchReferee"
                        options={officials.map(official => ({
                          label: `${official.firstName} ${official.lastName}`,
                          id: official.id,
                        }))}
                        emptyOption={'Select a referee'}
                        value={selectedOfficial}
                        themeV3
                        controlled={true}
                        onChange={(e) => handleRefereeChange(e)}
                        label="Referee"
                        data-testid="next-available.match-post-score.referee-select"
                    />
                    <EventDeskPostScore
                        eventId={eventId}
                        match={match}
                        sportTypeId={sportTypeId}
                        utrTypeId={utrTypeId}
                        playersDictByMemberId={playersDictByMemberId}
                        teamsDict={teamsDict}
                        clubTierType={clubTierType}
                        onScoreChanged={onScoreChanged}
                        onValidateScore={setScoresAreValid}
                        ref={postScoreRef}
                        data-testid="next-available.match-post-score.post-score"
                    />
                </div>
            </div>
            <div className={`${styles.footer} d-flex`}>
                <button
                    className={'btn btn-default mr8 flex-1'}
                    type="button"
                    onClick={closeSideBar}
                    data-testid="next-available.match-post-score.cancel-btn"
                >
                    Cancel
                </button>
                <button
                    className={`btn btn-primary flex-1`}
                    onClick={onSubmitClick}
                    type="button"
                    disabled={isDisabled}
                    data-testid="next-available.match-post-score.save-btn"
                >
                    Save
                </button>
            </div>
        </NextAvailableSidePanel>
    );
};

MatchPostScoreSidePanel.propTypes = {
    className: PropTypes.string,
    eventId: PropTypes.number.isRequired,
    closeSideBar: PropTypes.func,
    onPostSubmit: PropTypes.func.isRequired,
    match: PropTypes.objectOf(any).isRequired,
    officials: PropTypes.arrayOf(any).isRequired,
    sportTypeId: PropTypes.number.isRequired,
    utrTypeId: PropTypes.number,
    clubTierType: PropTypes.objectOf(any).isRequired,
    isSubmitting: PropTypes.bool,
    savingError: PropTypes.string,
    playersDictByMemberId: PropTypes.objectOf(any),
    teamsDict: PropTypes.objectOf(any),
};

MatchPostScoreSidePanel.defaultProps = {
    className: '',
    closeSideBar: () => {},
    isSubmitting: false,
    savingError: null,
    playersDictByMemberId: {},
    teamsDict: {},
    utrTypeId: utrTypeIds.MYUTR,
};

export default MatchPostScoreSidePanel;
